import { useState, useEffect } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { getPaginationUlStyles, getPaginationLiStyles } from './PaginationStyles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { Page } from '../Page/Page.js';
import { Icon } from '../Icon/Icon.js';

const Pagination = _ref => {
  let {
    totalPages,
    currentPage,
    boundaryPagesToShow = 7,
    siblingCount = 2,
    navAriaLabel = 'Pagination',
    pagesAriaLabel = 'Go to page',
    onPageChange,
    ...rest
  } = _ref;
  const dotsAllowed = totalPages - boundaryPagesToShow >= siblingCount;
  const pages = Array.from(Array(totalPages).keys()).map(page => page + 1);
  const [selectedPage, setSelectedPage] = useState(currentPage || pages[0]);
  const onPageClick = page => {
    setSelectedPage(page);
    onPageChange(page);
  };
  useEffect(() => {
    if (currentPage) {
      setSelectedPage(currentPage);
    }
  }, [currentPage]);

  /**
   * @param page number
   * @returns boolean
   * Check that the page is one of the first before the dots appear
   */
  const isPageBeforeDots = page => {
    const firstPagesBeforeDots = boundaryPagesToShow - siblingCount;
    return selectedPage <= firstPagesBeforeDots && page <= boundaryPagesToShow;
  };

  /**
   * @param page number
   * @returns boolean
   * Same as "isPageBeforeDots" but here we check that page is one of the lastests
   * when the dots disappear
   */
  const isPageAfterDots = page => {
    const lastPages = totalPages - boundaryPagesToShow;
    const lastPagesAfterDots = lastPages + siblingCount;
    return selectedPage > lastPagesAfterDots && page > lastPages;
  };
  const canRenderPage = page => {
    const isFirstPage = page === 1;
    const isLastPage = page === totalPages;
    const inPagesBefore = page >= selectedPage - siblingCount;
    const inPagesAfter = page <= selectedPage + siblingCount;
    const inMiddlePages = inPagesBefore && inPagesAfter && !isPageBeforeDots(selectedPage) && !isPageAfterDots(selectedPage);
    return !isFirstPage && !isLastPage && (isPageBeforeDots(page) || isPageAfterDots(page) || inMiddlePages);
  };
  const pagesToRender = pages.filter(canRenderPage);
  return jsx("nav", {
    "aria-label": navAriaLabel,
    ...rest,
    children: jsxs("ol", {
      css: getPaginationUlStyles(),
      children: [jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          withIcon: true,
          disabled: selectedPage === 1,
          onClick: () => onPageClick(selectedPage - 1),
          ariaLabel: selectedPage > 1 ? `${pagesAriaLabel} ${selectedPage - 1}` : undefined,
          children: jsx(Icon, {
            name: IconNames.CHEVRON_LEFT
          })
        })
      }, "prev-page"), jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          onClick: () => onPageClick(1),
          selected: selectedPage === 1,
          ariaLabel: selectedPage > 1 ? `${pagesAriaLabel} 1` : undefined,
          children: "1"
        })
      }, "page-1"), !isPageBeforeDots(selectedPage) && dotsAllowed && jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          selectable: false,
          children: "..."
        })
      }, "page-dots-1"), pagesToRender.map(page => jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          selected: selectedPage === page,
          onClick: () => onPageClick(page),
          ariaLabel: page !== selectedPage ? `${pagesAriaLabel} ${page}` : undefined,
          children: page
        })
      }, `page-${page}`)), !isPageAfterDots(selectedPage) && dotsAllowed && jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          selectable: false,
          children: "..."
        })
      }, "page-dots-2"), totalPages > 1 && jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          ariaLabel: `${pagesAriaLabel} ${totalPages}`,
          selected: selectedPage === totalPages,
          onClick: () => onPageClick(totalPages),
          children: totalPages
        })
      }, `page-${totalPages}`), jsx("li", {
        css: getPaginationLiStyles(),
        children: jsx(Page, {
          withIcon: true,
          disabled: totalPages === selectedPage,
          onClick: () => onPageClick(selectedPage + 1),
          ariaLabel: selectedPage < totalPages ? `${pagesAriaLabel} ${selectedPage + 1}` : undefined,
          children: jsx(Icon, {
            name: IconNames.CHEVRON_RIGHT
          })
        })
      }, "next-page")]
    })
  });
};

export { Pagination };
//# sourceMappingURL=Pagination.js.map
